import {useContext, useEffect, useState} from 'react';
import {AxiosError} from 'axios';
import {css} from '@emotion/css';
import {useQuery} from '@tanstack/react-query';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Modal from 'patient-ping-remedy/packages/modal';
import DropdownList from 'patient-ping-remedy/packages/dropdown-list';
import {Value} from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import {BannerTypes} from 'patient-ping-remedy/packages/banner';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {QueueMappingResponse} from '@/api/dto/connect';
import {
  QueueDetails,
  QueueDropdownWrapper,
  StyledBanner,
} from '@/components/pages/settings/telephony/QueueMappingForm.styles';

type Props = {
  isOpen: boolean;
  close: () => void;
  onSave: (rosterId: number, queueId: string, queueName: string) => void;
  isSaving: boolean;
  saveError: AxiosError | null;
  existingMappings: QueueMappingResponse[];
  resetError: () => void;
}

export default function QueueMappingForm(
  { isOpen, close, onSave, isSaving, saveError, existingMappings, resetError }: Props
) {
  const { carecoApi } = useContext(CarecoApiContext);

  const { data: availableQueues = [], isLoading: isLoadingQueues } = useQuery({
    queryKey: ['availableQueues'],
    queryFn: () => carecoApi?.getAvailableQueues(),
    enabled: !!carecoApi,
  });

  const [selectedRoster, setSelectedRoster] = useState<Value | null>(null);
  const [selectedQueue, setSelectedQueue] = useState<Value | null>(null);
  const [rosterOptions, setRosterOptions] = useState<Value[]>([]);
  const [isLoadingRosters, setIsLoadingRosters] = useState(false);

  useEffect(() => {
    if (isOpen && carecoApi) {
      setIsLoadingRosters(true);
      carecoApi.getRosterOptions()
        .then(setRosterOptions)
        .finally(() => setIsLoadingRosters(false));
    }
  }, [isOpen, carecoApi]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedRoster(null);
      setSelectedQueue(null);
    }
  }, [isOpen]);

  const mappedRosterIds = new Set(existingMappings.map((m) => m.roster.id.toString()));
  const availableRosterOptions: Value[] = rosterOptions.filter(
    (option) => !mappedRosterIds.has(option.value as string),
  );

  const queueOptions: Value[] = availableQueues.map((queue) => ({
    label: queue.queueName,
    value: queue.queueId,
  }));

  const is409 = saveError?.response?.status === 409;

  const handleSave = () => {
    if (selectedRoster && selectedQueue) {
      const queue = availableQueues.find(
        (q) => q.queueId === selectedQueue.value
      );
      onSave(
        Number(selectedRoster.value),
        selectedQueue.value as string,
        queue?.queueName ?? (selectedQueue.label as string),
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      hideModal={close}
      headerText="Add Queue Mapping"
      className={css({ width: '70rem' })}
      buttons={[
        <Button
          key="cancel"
          styleType={StyleType.TERTIARY}
          onClick={close}
        >
          Cancel
        </Button>,
        <Button
          key="save"
          onClick={handleSave}
          disabled={!selectedRoster || !selectedQueue || isSaving}
        >
          Save
        </Button>,
      ]}
    >
      {is409 && (
        <StyledBanner type={BannerTypes.WARNING}>
          This group already has a queue mapping. Delete the existing mapping first, then create a new one.
        </StyledBanner>
      )}
      <DropdownList
        id="roster-select"
        label="Group"
        required={true}
        value={selectedRoster}
        items={availableRosterOptions}
        handleChange={(value: Value | null) => {
          setSelectedRoster(value);
          resetError();
        }}
        placeholder="Select a group..."
        loading={isLoadingRosters}
      />
      <QueueDropdownWrapper>
        <DropdownList
          id="queue-select"
          label="Queue"
          required={true}
          value={selectedQueue}
          items={queueOptions}
          handleChange={(value: Value | null) => {
            setSelectedQueue(value);
            resetError();
          }}
          placeholder="Select a queue..."
          loading={isLoadingQueues}
          disabled={!selectedRoster}
        />
      </QueueDropdownWrapper>
      {selectedQueue && (
        <QueueDetails>
          <div>Queue Name: <strong>{selectedQueue.label}</strong></div>
          <div>Queue ARN: <strong>{selectedQueue.value}</strong></div>
        </QueueDetails>
      )}
    </Modal>
  );
}
